import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchTheme, fetchThemes, fetchTasks, createTask, updateTask, deleteTask, deleteTheme, createTheme } from '../api/projects';
import { fetchPeople } from '../api/people';
import type { Theme, Task } from '../types/project';
import type { Person } from '../types/people';
import KanbanBoard from '../components/KanbanBoard';
import TaskList from '../components/TaskList';
import AssigneeMultiPicker from '../components/AssigneeMultiPicker';
import type { TaskStatus, TaskPriority } from '../types/project';
import { TASK_STATUSES, STATUS_LABEL } from '../utils/taskStatus';

type ViewMode = 'list' | 'board';

export default function ThemeDetailPage() {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme | null>(null);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | 'ALL'>('ALL');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newStatus, setNewStatus] = useState<TaskStatus>('TO_DO');
  const [newPriority, setNewPriority] = useState<TaskPriority>('MEDIUM');
  const [newAssignees, setNewAssignees] = useState<string[]>([]);
  const [newDueDate, setNewDueDate] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [subThemeFormOpen, setSubThemeFormOpen] = useState(false);
  const [newSubThemeName, setNewSubThemeName] = useState('');
  const [newSubThemeDescription, setNewSubThemeDescription] = useState('');
  const [creatingSubTheme, setCreatingSubTheme] = useState(false);
  const [subThemeError, setSubThemeError] = useState('');

  const load = useCallback((showLoading = false) => {
    if (!themeId) return;
    if (showLoading) setLoading(true);
    Promise.all([fetchTheme(themeId), fetchTasks(themeId), fetchPeople(), fetchThemes()])
      .then(([t, ts, p, allThemes]) => {
        setTheme(t);
        setTasks(ts);
        setPeople(p);
        setThemes(allThemes);
      })
      .catch((e) => setError(e.message))
      .finally(() => {
        if (showLoading) setLoading(false);
      });
  }, [themeId]);

  useEffect(() => {
    // Show the loading screen only on initial entry / themeId change.
    load(true);
  }, [load]);

  const handleAddTask = useCallback(() => {
    setNewTitle('');
    setNewStatus('TO_DO');
    setNewPriority('MEDIUM');
    setNewAssignees([]);
    setNewDueDate('');
    setDrawerOpen(true);
  }, []);

  const handleAddTaskToColumn = useCallback((status: TaskStatus) => {
    setNewStatus(status);
    setNewTitle('');
    setNewPriority('MEDIUM');
    setNewAssignees([]);
    setNewDueDate('');
    setDrawerOpen(true);
  }, []);

  const handleCreateTask = useCallback(async () => {
    if (!themeId || !newTitle.trim()) return;
    try {
      await createTask({
        theme_id: themeId,
        title: newTitle.trim(),
        status: newStatus,
        priority: newPriority,
        assignee_ids: newAssignees,
        due_date: newDueDate || undefined,
      });
      setNewTitle('');
      setNewAssignees([]);
      setNewDueDate('');
      setDrawerOpen(false);
      load(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create task');
    }
  }, [themeId, newTitle, newStatus, newPriority, newAssignees, newDueDate, load]);

  const handleStatusChange = useCallback(
    async (taskId: string, status: TaskStatus) => {
      try {
        await updateTask(taskId, { status });
        setTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, status } : t))
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update task');
      }
    },
    []
  );

  const handlePriorityChange = useCallback(
    async (taskId: string, priority: TaskPriority) => {
      try {
        await updateTask(taskId, { priority });
        setTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, priority } : t))
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update task');
      }
    },
    []
  );

  const handleAssigneesChange = useCallback(
    async (taskId: string, assigneeIds: string[]) => {
      try {
        await updateTask(taskId, { assignee_ids: assigneeIds });
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  assignee_ids: assigneeIds,
                  assignee: assigneeIds.length > 0 ? assigneeIds[0] : null,
                }
              : t
          )
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update task assignees');
      }
    },
    []
  );

  const handleCancelTask = useCallback(
    async (task: Task) => {
      try {
        await deleteTask(task.id);
        load(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to cancel task');
      }
    },
    [load]
  );

  const handleDeleteTheme = useCallback(() => {
    if (!themeId || !theme) return;
    if (!window.confirm('Are you sure?')) return;
    deleteTheme(themeId)
      .then(() => navigate('/projects'))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to delete theme'));
  }, [themeId, theme, navigate]);

  const handleAddSubTheme = useCallback(() => {
    setNewSubThemeName('');
    setNewSubThemeDescription('');
    setSubThemeError('');
    setSubThemeFormOpen(true);
  }, []);

  const handleCreateSubTheme = useCallback(async () => {
    if (!themeId || !newSubThemeName.trim()) {
      setSubThemeError('Name is required');
      return;
    }
    setCreatingSubTheme(true);
    setSubThemeError('');
    try {
      await createTheme({
        name: newSubThemeName.trim(),
        description: newSubThemeDescription.trim() || undefined,
        parent_theme_id: themeId,
      });
      setSubThemeFormOpen(false);
      setNewSubThemeName('');
      setNewSubThemeDescription('');
      load(false);
    } catch (e) {
      setSubThemeError(e instanceof Error ? e.message : 'Failed to create sub-theme');
    } finally {
      setCreatingSubTheme(false);
    }
  }, [themeId, newSubThemeName, newSubThemeDescription, load]);

  if (loading || !theme) {
    return <div>{loading ? 'Loading…' : 'Theme not found'}</div>;
  }
  if (error) {
    return <div style={{ color: 'var(--color-coral)' }}>{error}</div>;
  }

  const isRootTheme = !theme.parent_theme_id;
  const parentTheme = theme.parent_theme_id ? themes.find((t) => t.id === theme.parent_theme_id) : null;
  const subThemes = themes.filter((t) => t.parent_theme_id === theme.id);

  const filteredTasks =
    priorityFilter === 'ALL'
      ? tasks
      : tasks.filter((t) => t.priority === priorityFilter);

  return (
    <>
      <nav style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
        <Link to="/projects" style={{ color: 'var(--color-blue-dark)' }}>Projects</Link>
        {parentTheme ? (
          <>
            {' / '}
            <Link to={`/projects/theme/${parentTheme.id}`} style={{ color: 'var(--color-blue-dark)' }}>{parentTheme.name}</Link>
            {' / '}
            <span>{theme.name}</span>
          </>
        ) : (
          <>
            {' / '}
            <span>{theme.name}</span>
          </>
        )}
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: 700, margin: 0 }}>{theme.name}</h1>
        <button
          type="button"
          onClick={handleDeleteTheme}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--color-coral)',
            background: 'transparent',
            border: '1px solid var(--color-coral)',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
          }}
        >
          Delete theme
        </button>
      </div>
      {theme.description && (
        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '24px' }}>{theme.description}</p>
      )}

      {isRootTheme ? (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Sub-themes</h2>
            <button
              type="button"
              onClick={handleAddSubTheme}
              style={{
                padding: '8px 16px',
                background: 'var(--color-blue-dark)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius)',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Add sub-theme
            </button>
          </div>
          {subThemes.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)' }}>No sub-themes yet. Click “Add sub-theme” to create one.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {subThemes.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => navigate(`/projects/theme/${sub.id}`)}
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px',
                    borderRadius: 'var(--radius)',
                    border: '1px solid var(--color-border)',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{sub.name}</div>
                  {sub.description && (
                    <div style={{ marginTop: '4px', color: 'var(--color-text-secondary)', fontSize: '13px' }}>{sub.description}</div>
                  )}
                </button>
              ))}
            </div>
          )}
          {subThemeFormOpen && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.3)',
                zIndex: 100,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
              onClick={() => setSubThemeFormOpen(false)}
              role="presentation"
            >
              <div
                style={{
                  width: '520px',
                  background: 'white',
                  boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
                  padding: '24px',
                  overflowY: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 style={{ margin: '0 0 16px' }}>New sub-theme</h2>
                {subThemeError && (
                  <p style={{ color: 'var(--color-coral)', marginBottom: '16px', fontSize: '14px' }}>{subThemeError}</p>
                )}
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Name</label>
                <input
                  value={newSubThemeName}
                  onChange={(e) => setNewSubThemeName(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                  placeholder="Sub-theme name"
                />
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Description (optional)</label>
                <textarea
                  value={newSubThemeDescription}
                  onChange={(e) => setNewSubThemeDescription(e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '24px', resize: 'vertical' }}
                  placeholder="Brief description"
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleCreateSubTheme}
                    disabled={creatingSubTheme || !newSubThemeName.trim()}
                    style={{
                      padding: '10px 20px',
                      background: 'var(--color-blue-dark)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius)',
                      fontWeight: 600,
                      cursor: creatingSubTheme ? 'not-allowed' : 'pointer',
                      opacity: creatingSubTheme || !newSubThemeName.trim() ? 0.7 : 1,
                    }}
                  >
                    {creatingSubTheme ? 'Creating…' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSubThemeFormOpen(false); setSubThemeError(''); }}
                    style={{ padding: '10px 20px', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  background: viewMode === 'list' ? 'var(--color-blue-dark)' : 'white',
                  color: viewMode === 'list' ? 'white' : 'var(--color-text-primary)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('board')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  background: viewMode === 'board' ? 'var(--color-blue-dark)' : 'white',
                  color: viewMode === 'board' ? 'white' : 'var(--color-text-primary)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Board
              </button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
              Priority:
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | 'ALL')}
                style={{
                  padding: '6px 10px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius)',
                  fontSize: '14px',
                  background: 'white',
                }}
              >
                <option value="ALL">All</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </label>
          </div>

          {viewMode === 'list' ? (
            <TaskList
              tasks={filteredTasks}
              people={people}
              onTaskClick={(task) => navigate(`/projects/task/${task.id}`)}
              onAddTask={handleAddTask}
              onCancelTask={handleCancelTask}
              onStatusChange={handleStatusChange}
              onPriorityChange={handlePriorityChange}
              onAssigneesChange={handleAssigneesChange}
            />
          ) : (
            <KanbanBoard
              tasks={filteredTasks}
              people={people}
              onTaskClick={(task) => navigate(`/projects/task/${task.id}`)}
              onStatusChange={handleStatusChange}
              onPriorityChange={handlePriorityChange}
              onAssigneesChange={handleAssigneesChange}
              onAddTask={handleAddTaskToColumn}
              showAddCard
            />
          )}

          {drawerOpen && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.3)',
                zIndex: 100,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
              onClick={() => setDrawerOpen(false)}
              role="presentation"
            >
              <div
                style={{
                  width: '520px',
                  background: 'white',
                  boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
                  padding: '24px',
                  overflowY: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h2 style={{ margin: '0 0 16px' }}>New task</h2>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Title</label>
                <input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                  placeholder="Task title"
                />
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as TaskStatus)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                >
                  {TASK_STATUSES.map((s) => (
                    <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                  ))}
                </select>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Priority</label>
                <select
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as TaskPriority)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '16px' }}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
                <AssigneeMultiPicker
                  people={people}
                  value={newAssignees}
                  onChange={setNewAssignees}
                  label="Assignees"
                />
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600 }}>Due date</label>
                <input
                  type="date"
                  value={newDueDate}
                  onChange={(e) => setNewDueDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: '24px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleCreateTask}
                    style={{ padding: '10px 20px', background: 'var(--color-blue-dark)', color: 'white', border: 'none', borderRadius: 'var(--radius)', fontWeight: 600 }}
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(false)}
                    style={{ padding: '10px 20px', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
